<template>
  <v-dialog v-model="show" width="700" persistent no-click-animation>
    <v-card>
      <v-toolbar flat height="55">
        <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">Historial de eventos</v-toolbar-title>
        <v-spacer />
        <v-toolbar-items>
          <v-row align="center" justify="center" class="mr-1">
            <v-btn @click="show=false" icon small><v-icon size="24">mdi-close</v-icon></v-btn>
          </v-row>
        </v-toolbar-items>
      </v-toolbar>
      <v-divider />
      <v-card-text class="px-0 py-0 background">
        <v-skeleton-loader v-if="loader" transition="fade-transition" type="table" />
        <template v-else>
          <v-data-table v-if="eventsList.length" :headers="headers" :items="eventsList" hide-default-header hide-default-footer disable-sort mobile-breakpoint>
            <!-- header -->
            <template v-slot:header="{ props: { headers } }">
              <VTableHeaders :headers="headers" />
            </template>
            <!-- end header -->
            <template v-slot:[`item.created`]="{item}">
              <span class="text--secondary">{{ formatDate(item.created) }}</span>
            </template>
            <template v-slot:[`item.action`]="{item}">
              <v-chip :color="actionColor(item.action)" :text-color="actionTextColor(item.action)" small>{{ actionLabel(item.action) }}</v-chip>
            </template>
            <template v-slot:[`item.user`]="{item}">
              <span v-if="item.user">{{ item.user }}</span>
              <span v-else class="text--disabled font-italic">Automático</span>
            </template>
            <template v-slot:[`item.details`]="{item}">
              <span class="body-2" style="white-space: normal;">{{ formatDetails(item) }}</span>
            </template>
          </v-data-table>
          <v-sheet v-else class="py-8 text-center">
            <v-icon class="d-block" color="secondary" size="70">mdi-timeline-outline</v-icon>
            <span class="d-block subtitle-1">No hay eventos registrados</span>
          </v-sheet>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import VTableHeaders from '@/components/VTable/VTableHeaders'
import { cancelReasons, pauseReasons } from '@/collections/subscriptions'

moment.locale('es')

const allReasons = [...cancelReasons, ...pauseReasons]

const actionConfig = {
  created: { label: 'Creada', color: 'lightBlue', textColor: 'primary' },
  paused: { label: 'Pausada', color: 'orange lighten-4', textColor: 'orange darken-3' },
  reactivated: { label: 'Reactivada', color: 'green lighten-5', textColor: 'green darken-2' },
  cancelled: { label: 'Cancelada', color: 'red lighten-5', textColor: 'red darken-2' },
  needs_attention: { label: 'Necesita atención', color: 'defaultGrey', textColor: 'secondary' },
  updated: { label: 'Actualizada', color: 'defaultGrey', textColor: 'secondary' }
}

export default {
  components: { VTableHeaders },
  props: {
    value: {
      type: Boolean,
      required: true
    },
    subscriptionId: {
      type: String,
      required: true
    }
  },
  data: () => ({
    eventsList: [],
    loader: false,
    headers: [
      { text: 'Fecha', value: 'created' },
      { text: 'Acción', value: 'action' },
      { text: 'Usuario', value: 'user' },
      { text: 'Detalles', value: 'details', sortable: false }
    ]
  }),
  computed: {
    show: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    }
  },
  watch: {
    value (val) {
      if (val) this.fetchEvents()
    }
  },
  methods: {
    fetchEvents () {
      this.loader = true
      this.$store.dispatch('subscriptions/RETRIEVE', {
        resource: 'subscriptions',
        id: this.subscriptionId,
        detail: 'events',
        setDetail: false,
        loader: false
      })
      .then(response => {
        this.eventsList = response.data
      })
      .catch(() => {
        this.eventsList = []
      })
      .finally(() => {
        this.loader = false
      })
    },
    actionLabel (action) {
      return actionConfig[action]?.label || action
    },
    actionColor (action) {
      return actionConfig[action]?.color || 'defaultGrey'
    },
    actionTextColor (action) {
      return actionConfig[action]?.textColor || 'secondary'
    },
    formatDate (val) {
      return moment(val).format('DD MMM YYYY HH:mm')
    },
    formatDetails (event) {
      const { action, details } = event

      if (!details || Object.keys(details).length === 0) {
        return '—'
      }

      if (action === 'paused') {
        const reason = allReasons.find(r => r.value === details.reason)?.label || details.reason
        let text = `Razón: ${reason}`

        if (details.pause_until) {
          text += ` · Hasta: ${moment(details.pause_until).format('DD MMM YYYY')}`
        }

        return text
      }

      if (action === 'reactivated') {
        const parts = []

        if (details.next_billing) {
          parts.push(`Próx. facturación: ${moment(details.next_billing).format('DD MMM YYYY')}`)
        }

        if (details.source === 'auto_resume') {
          parts.push('Reactivación automática')
        }

        return parts.length ? parts.join(' · ') : '—'
      }

      if (action === 'cancelled') {
        const reason = allReasons.find(r => r.value === details.reason)?.label || details.reason
        return `Razón: ${reason}`
      }

      if (action === 'updated' && details.changed_fields) {
        const fields = Object.keys(details.changed_fields)
        return `Campos: ${fields.join(', ')}`
      }

      return '—'
    }
  }
}
</script>
